import { useState } from "react";
import { toast } from "sonner";
import { completeSignatureFromClient } from "../../api/discussion";

type SignatureFromClientViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

/**
 * Signature from Client — Signature Process
 * Figma node: 97-3775
 * Layout: Frame 1000005753 — flex col, center, gap 20px; 1085px max-width
 */
export default function SignatureFromClientView({
  leadId,
  onSuccess,
}: SignatureFromClientViewProps) {
  const [saving, setSaving] = useState(false);

  const handleSaveAndContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    setSaving(true);
    try {
      await completeSignatureFromClient(leadId);
      toast.success("Signature from client saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this step.
        </p>
      </div>
    );
  }

  return (
    <form
      id="signature-from-client-form"
      onSubmit={handleSaveAndContinue}
      className="flex flex-col items-center w-full"
      style={{
        fontFamily: "Inter, sans-serif",
        fontFeatureSettings: "'ss01' on, 'cv01' on",
      }}
    >
      <div
        className="flex flex-col items-center w-full max-w-[1085px]"
        style={{ gap: 20 }}
      >
        {/* Signature Process — 16px, #222222 */}
        <div
          className="flex items-center text-[#222222] flex-none"
          style={{
            fontWeight: 400,
            fontSize: 16,
            lineHeight: "20px",
          }}
        >
          Signature Process
        </div>

        {/* Frame 1000005759 — instruction + checklist */}
        <div
          className="flex flex-row justify-center items-center w-full"
          style={{ gap: 20, flex: "none" }}
        >
          {/* Frame 1000005750 — instruction + checklist column */}
          <div
            className="flex flex-col justify-center items-center flex-1 max-w-[427px]"
            style={{ gap: 20 }}
          >
            {/* Instruction text — 12px, center, #222222 */}
            <div
              className="flex items-center text-center text-[#222222] w-full"
              style={{
                fontWeight: 400,
                fontSize: 12,
                lineHeight: "20px",
              }}
            >
              Please ensure the Power of Attorney (Form 48) and User Affidavit
              (if applicable) are printed on the stamp papers procured in Step 5
              and signed by the client.
            </div>

            {/* Frame 1000005748 — Checklist box */}
            <div
              className="box-border flex flex-col items-start w-full flex-none"
              style={{
                padding: 10,
                gap: 10,
                background: "#F7F7F7",
                border: "1px solid #EEEEEE",
                borderRadius: 6,
                minHeight: 130,
              }}
            >
              <div
                className="flex items-center text-[#6D6D6D] flex-none"
                style={{
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: "20px",
                }}
              >
                Checklist:
              </div>
              <ul
                className="flex flex-col gap-1 list-none pl-0 m-0 w-full"
                style={{
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: "20px",
                  color: "#6D6D6D",
                }}
              >
                <li className="flex items-start gap-2">
                  <span
                    className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-[#6D6D6D]"
                    aria-hidden
                  />
                  <span>Print PoA on Stamp Paper</span>
                </li>
                <li className="flex items-start gap-2">
                  <span
                    className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-[#6D6D6D]"
                    aria-hidden
                  />
                  <span>Print User Affidavit on Stamp Paper (if not &quot;Proposed&quot;)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span
                    className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-[#6D6D6D]"
                    aria-hidden
                  />
                  <span>Obtain Physical Signatures/Seal</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Save & Continue is provided by the parent footer */}
      </div>
    </form>
  );
}
